import { Loader2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { PageShell } from "@/components/page-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { track, trackOnce } from "@/lib/analytics";
import {
  type AnswerSuggestion,
  type AssessmentCreateRequest,
  type DefinitionLayer,
  useAssessmentDefinition,
  useCreateAssessment,
  useMe,
} from "@/lib/api";
import { logger } from "@/lib/logger";

import { CompanyBasics } from "./company-basics";
import { type AssessmentDraft, clearDraft, type DraftStatus, loadDraft, saveDraft } from "./draft";
import { PrefillStep } from "./prefill-step";
import { QuestionWizard } from "./question-wizard";
import { ResultsGate } from "./results-gate";
import { ReviewStep } from "./review-step";
import type { CompanyMeta, Step, WizardScreen } from "./types";
import { UnlockConfirm } from "./unlock-confirm";

export function AssessPage() {
  const definitionQuery = useAssessmentDefinition();

  return (
    <PageShell>
      {definitionQuery.isLoading && (
        <div className="flex justify-center py-24">
          <Loader2 className="text-muted h-6 w-6 animate-spin" />
        </div>
      )}
      {definitionQuery.error != null && (
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground text-sm">
              Couldn’t load the assessment. Please try again.
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => void definitionQuery.refetch()}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      )}
      {definitionQuery.data && (
        <AssessmentFlow
          layers={definitionQuery.data.layers}
          totalQuestions={definitionQuery.data.total_questions}
        />
      )}
    </PageShell>
  );
}

const EMPTY_META: CompanyMeta = {
  company_name: "",
  company_url: "",
  sector: "",
  subsector: "",
  geography: "",
  company_size: "medium",
  business_model_type: "",
  respondent_role: "",
  free_text_context: "",
};

interface AssessmentFlowProps {
  layers: DefinitionLayer[];
  totalQuestions: number;
}

function AssessmentFlow({ layers, totalQuestions }: AssessmentFlowProps) {
  const navigate = useNavigate();
  const createMutation = useCreateAssessment();

  // Three distinct auth states, not just "have me / don't". A non-401 /auth/me
  // failure (network, 5xx) must NOT be read as anonymous: a logged-in user
  // whose session check hiccups should be offered a retry, never the sign-in
  // gate (which would strand them / mis-route their submission).
  const meQuery = useMe();
  const me = meQuery.data;
  const authLoading = meQuery.isLoading;
  const meStatus = (meQuery.error as { status?: number } | null)?.status;
  const isAnonymous = !authLoading && !me && meStatus === 401;
  const authCheckFailed = !authLoading && !me && !!meQuery.error && meStatus !== 401;

  // The set of question ids the current definition still recognizes, used to
  // prune answers a stale draft may hold for questions that no longer exist.
  const validQuestionIds = useMemo(() => {
    const ids = new Set<string>();
    for (const layer of layers) {
      for (const dim of layer.dimensions) {
        for (const q of dim.questions) {
          ids.add(q.question_id);
        }
      }
    }
    return ids;
  }, [layers]);

  // Restore any saved draft exactly once, at mount, via lazy initializers so the
  // wizard comes up on the right step with the user's answers already in place.
  // Answers/suggestions for questions the definition dropped are pruned; if that
  // prunes a submit-pending draft, it's downgraded to a re-review so we never
  // unlock (and 422-loop on) stale answers.
  const initial = useMemo<{ draft: AssessmentDraft | null; pruned: boolean }>(() => {
    const raw = loadDraft();
    if (!raw) {
      return { draft: null, pruned: false };
    }
    const answers: Record<string, number> = {};
    let pruned = false;
    for (const [qid, value] of Object.entries(raw.answers)) {
      if (validQuestionIds.has(qid)) {
        answers[qid] = value;
      } else {
        pruned = true;
      }
    }
    const suggestions: Record<string, AnswerSuggestion> = {};
    for (const [qid, suggestion] of Object.entries(raw.suggestions)) {
      if (validQuestionIds.has(qid)) {
        suggestions[qid] = suggestion;
      }
    }
    const downgrade = pruned && raw.status === "submit-pending";
    return {
      draft: {
        ...raw,
        answers,
        suggestions,
        status: downgrade ? "in-progress" : raw.status,
        step: downgrade ? "review" : raw.step,
      },
      pruned,
    };
  }, [validQuestionIds]);
  const initialDraft = initial.draft;

  const [step, setStep] = useState<Step>(initialDraft?.step ?? "basics");
  const [meta, setMeta] = useState<CompanyMeta>(initialDraft?.meta ?? EMPTY_META);
  const [answers, setAnswers] = useState<Record<string, number>>(initialDraft?.answers ?? {});
  const [suggestions, setSuggestions] = useState<Record<string, AnswerSuggestion>>(
    initialDraft?.suggestions ?? {},
  );
  const [prefillSummary, setPrefillSummary] = useState<string | null>(
    initialDraft?.prefillSummary ?? null,
  );
  const [draftStatus, setDraftStatus] = useState<DraftStatus>(
    initialDraft?.status ?? "in-progress",
  );
  const [resumeQuestionId, setResumeQuestionId] = useState<string | null>(
    initialDraft?.resumeQuestionId ?? null,
  );

  // Contextual banner offering a "Start over" escape. "restored" reassures a
  // returning user; "review" flags that they should re-check their answers
  // (stale draft pruned at restore, or a rejected submission).
  const [noticeReason, setNoticeReason] = useState<"restored" | "review" | null>(() => {
    if (!initialDraft) {
      return null;
    }
    if (initial.pruned) {
      return "review";
    }
    return initialDraft.status === "in-progress" ? "restored" : null;
  });

  // Wizard starts on this question id: the resume point on restore, or the
  // target of an "Edit" from the review step.
  const [startQuestionId, setStartQuestionId] = useState<string | undefined>(
    initialDraft?.step === "wizard" ? (initialDraft.resumeQuestionId ?? undefined) : undefined,
  );

  const [error, setError] = useState<string | null>(null);
  const [unlocking, setUnlocking] = useState(false);
  const submitFiredRef = useRef(false);

  const handleQuestionChange = useCallback((questionId: string) => {
    setResumeQuestionId(questionId);
    track("question_viewed", { question_id: questionId });
  }, []);

  // Persist on every meaningful change so a refresh or auth round-trip can't
  // drop the user's work. Skip the pristine initial state to avoid seeding an
  // empty draft (which would otherwise trigger a spurious restore notice).
  useEffect(() => {
    const meaningful =
      draftStatus === "submit-pending" ||
      step !== "basics" ||
      meta.company_name.trim() !== "" ||
      Object.keys(answers).length > 0;
    if (!meaningful) {
      return;
    }
    saveDraft({
      savedAt: new Date().toISOString(),
      status: draftStatus,
      step,
      meta,
      answers,
      suggestions,
      prefillSummary,
      resumeQuestionId,
    });
  }, [step, meta, answers, suggestions, prefillSummary, draftStatus, resumeQuestionId]);

  // Build the ordered wizard screen sequence (intros + questions).
  const screens = useMemo<WizardScreen[]>(() => {
    const out: WizardScreen[] = [];
    let qNum = 0;
    for (const layer of layers) {
      const qCount = layer.dimensions.reduce((m, d) => m + d.questions.length, 0);
      out.push({
        type: "intro",
        layer: layer.layer,
        title: layer.title,
        dimensionCount: layer.dimensions.length,
        questionCount: qCount,
      });
      for (const dim of layer.dimensions) {
        for (const q of dim.questions) {
          qNum += 1;
          out.push({
            type: "question",
            layer: layer.layer,
            layerTitle: layer.title,
            dimensionName: dim.dimension_name,
            description: dim.description,
            question: q,
            questionNumber: qNum,
          });
        }
      }
    }
    return out;
  }, [layers]);

  function applySuggestions(result: { summary: string; suggestions: AnswerSuggestion[] }) {
    const byId: Record<string, AnswerSuggestion> = {};
    const seededAnswers: Record<string, number> = { ...answers };
    for (const s of result.suggestions) {
      byId[s.question_id] = s;
      seededAnswers[s.question_id] = s.value;
    }
    setSuggestions(byId);
    setAnswers(seededAnswers);
    setPrefillSummary(result.summary);
  }

  function setAnswer(questionId: string, value: number) {
    setAnswers((a) => ({ ...a, [questionId]: value }));
  }
  function clearAnswer(questionId: string) {
    setAnswers((a) => {
      const next = { ...a };
      delete next[questionId];
      return next;
    });
  }

  // Shared submission core. Reached two ways: a logged-in user pressing
  // "Generate report" (createMutation.isPending drives the button), or the
  // explicit "Unlock my report" confirmation after signing in (`unlocking`
  // drives a full-screen spinner). On success the draft is cleared and we
  // navigate to the report; on failure we drop back to review with the error.
  async function runSubmit() {
    setError(null);
    try {
      const data = await createMutation.mutateAsync({
        ...meta,
        // Form values come from COMPANY_SIZES, which matches the CompanySize literal union.
        company_size: meta.company_size as AssessmentCreateRequest["company_size"],
        answers: Object.entries(answers).map(([question_id, value]) => ({ question_id, value })),
        prefill_summary: prefillSummary ?? undefined,
        prefill_suggestions: Object.values(suggestions),
      });
      if (!data) {
        throw new Error("Submission failed.");
      }
      logger.debug("[assess] created assessment", data.assessment_id);
      track("assessment_submitted");
      clearDraft();
      navigate(`/report/${data.assessment_id}`);
    } catch (err) {
      logger.debug("[assess] submission failed:", err);
      // Reset the submit machinery so the user can retry from review.
      submitFiredRef.current = false;
      setUnlocking(false);
      setDraftStatus("in-progress");
      setStep("review");
      const status = (err as { status?: number } | undefined)?.status;
      const detail = (err as { body?: { detail?: string } } | undefined)?.body?.detail;
      if (status === 422) {
        // Stale answers can't be salvaged automatically, surface the error and
        // the "Start over" escape so the user isn't stuck in a re-submit loop.
        setError(
          "Some answers need another look before we can generate your report. Re-review and try again.",
        );
        setNoticeReason("review");
      } else {
        setError(typeof detail === "string" ? detail : "Submission failed.");
      }
    }
  }

  // "Generate report" from review, routed by auth state:
  //   authLoading    → wait (button is also disabled here)
  //   authCheckFailed→ surface a retry; never submit or gate on a flaky check
  //   isAnonymous    → gate (mark submit-pending, render the sign-in wall)
  //   me present     → submit straight away
  function handleGenerate() {
    if (authLoading) {
      return;
    }
    if (authCheckFailed) {
      logger.debug("[assess] /auth/me check failed (non-401) → surfacing retry, not gating");
      setError("Couldn't verify your sign-in status. Please retry.");
      void meQuery.refetch();
      return;
    }
    if (isAnonymous) {
      logger.debug("[assess] anonymous submit → gating behind sign-in");
      track("results_gate_shown");
      setDraftStatus("submit-pending");
      return;
    }
    void runSubmit();
  }

  // Explicit unlock: fired only by the user clicking "Unlock my report" on the
  // confirm screen (never automatically) so a stranger's abandoned draft is
  // never silently submitted under a freshly-authenticated account. The ref
  // guards against a double click firing two submissions.
  function handleUnlock() {
    if (submitFiredRef.current) {
      return;
    }
    submitFiredRef.current = true;
    setUnlocking(true);
    logger.debug("[assess] user confirmed unlock → submitting");
    void runSubmit();
  }

  function resetWizard() {
    clearDraft();
    submitFiredRef.current = false;
    setStep("basics");
    setMeta(EMPTY_META);
    setAnswers({});
    setSuggestions({});
    setPrefillSummary(null);
    setStartQuestionId(undefined);
    setResumeQuestionId(null);
    setDraftStatus("in-progress");
    setError(null);
    setNoticeReason(null);
  }

  // A submit-pending draft resolves by auth state: the in-flight unlock spinner,
  // a brief spinner while the session loads, a retry card if the /auth/me check
  // failed (transient, never the gate), the sign-in gate only when genuinely
  // anonymous, or the explicit unlock confirmation (authenticated). Submission
  // never fires on its own: an authenticated user is always asked to confirm.
  if (draftStatus === "submit-pending") {
    if (unlocking || authLoading) {
      return <UnlockingReport />;
    }
    if (me) {
      // Reaching this branch means an anonymous visitor gated behind sign-in,
      // signed in, and came back to a restored submit-pending draft. Fire once
      // per tab; this render path re-runs on every subsequent state change.
      trackOnce("results_gate_signed_in", "results_gate_signed_in");
      return (
        <UnlockConfirm
          companyName={meta.company_name}
          onUnlock={handleUnlock}
          onDiscard={resetWizard}
        />
      );
    }
    if (authCheckFailed) {
      return <AuthCheckError onRetry={() => void meQuery.refetch()} />;
    }
    if (isAnonymous) {
      return <ResultsGate onBack={() => setDraftStatus("in-progress")} />;
    }
    // Indeterminate (no data, no 401, no other error): hold rather than gate.
    return <UnlockingReport />;
  }

  const noticeText =
    noticeReason === "restored"
      ? "Restored your in-progress assessment."
      : "Please re-review your answers before generating your report.";
  const restoreNotice = noticeReason ? (
    <div className="border-accent bg-accent-light/20 mb-6 flex items-center justify-between gap-4 rounded-md border px-4 py-3">
      <p className="text-primary text-sm">
        {noticeText}{" "}
        <button
          type="button"
          onClick={resetWizard}
          className="text-accent font-medium hover:underline"
        >
          Start over
        </button>
      </p>
      <button
        type="button"
        onClick={() => setNoticeReason(null)}
        className="text-muted hover:text-primary shrink-0 transition-colors"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  ) : null;

  if (step === "basics") {
    return (
      <>
        {restoreNotice}
        <CompanyBasics
          meta={meta}
          onChange={setMeta}
          onContinue={() => {
            // Once per tab: basics → prefill → Back → Continue must not re-count
            // the same funnel entry, and a second assessment after "Start over"
            // deliberately doesn't re-count either (matches the per-tab session id).
            trackOnce("assessment_started", "assessment_started");
            setStep("prefill");
          }}
        />
      </>
    );
  }

  if (step === "prefill") {
    return (
      <>
        {restoreNotice}
        <PrefillStep
          meta={meta}
          suggestionCount={Object.keys(suggestions).length}
          onApply={applySuggestions}
          onBack={() => setStep("basics")}
          onContinue={() => {
            track("step_advanced", { step: "wizard" });
            setStartQuestionId(undefined);
            setStep("wizard");
          }}
        />
      </>
    );
  }

  if (step === "wizard") {
    return (
      <>
        {restoreNotice}
        <QuestionWizard
          screens={screens}
          totalQuestions={totalQuestions}
          answers={answers}
          suggestions={suggestions}
          startQuestionId={startQuestionId}
          onAnswer={setAnswer}
          onSkip={clearAnswer}
          onQuestionChange={handleQuestionChange}
          onBack={() => setStep("prefill")}
          onComplete={() => {
            track("step_advanced", { step: "review" });
            setStep("review");
          }}
        />
      </>
    );
  }

  return (
    <>
      {restoreNotice}
      <ReviewStep
        layers={layers}
        answers={answers}
        suggestions={suggestions}
        totalQuestions={totalQuestions}
        submitting={createMutation.isPending}
        authLoading={authLoading}
        error={error}
        onEdit={(qid) => {
          setStartQuestionId(qid);
          setStep("wizard");
        }}
        onBack={() => {
          setStartQuestionId(undefined);
          setStep("wizard");
        }}
        onSubmit={handleGenerate}
      />
    </>
  );
}

/** Full-screen hold: submit in flight after confirming, or session state loading. */
function UnlockingReport() {
  return (
    <div className="flex flex-col items-center gap-3 py-24">
      <Loader2 className="text-accent h-6 w-6 animate-spin" />
      <p className="text-muted text-sm">Unlocking your report…</p>
    </div>
  );
}

/** Shown when the /auth/me check failed for a non-401 reason: offer a retry, not the gate. */
function AuthCheckError({ onRetry }: { onRetry: () => void }) {
  return (
    <Card className="mx-auto max-w-md">
      <CardContent className="space-y-4 pt-6 text-center">
        <p className="text-muted-foreground text-sm">
          Couldn’t verify your sign-in status. Please retry.
        </p>
        <Button variant="outline" onClick={onRetry}>
          Retry
        </Button>
      </CardContent>
    </Card>
  );
}
